import React from 'react';
import { handleActionError } from '../../../utils/engineering-tools';
import { GreaseFrequencyPayload, TimkenUnit } from '../../../../components/bearing-search/types/types';
import { convertNumberInput, isValidRange } from '../../../utils/validations';
import { greaseRelubricateLoad, lubricationConditions } from '../../../../components/bearing-search/api/api';

type RelubricateConditionsValidationMap = Map<keyof RelubricateConditionsState, boolean>;

export type YesNo = 'Yes' | 'No';

export type GreaseFrequencyConditionsPayload = Pick<GreaseFrequencyPayload,
	'temperature'
	| 'rpm'
	| 'loadDataID'
	| 'verticalShaftId'
	| 'oscillatingMovementsId'
	| 'shockLoadsId'
	| 'verticalShaftIdYesNo'
	| 'oscillatingMovementsIdYesNo'
	| 'shockLoadsIdYesNo'
	| 'vibrationShockLoad'
>;

export type RelubricateConditionsState = {
	loadDataID: number;
	temperature?: number;
	rpm?: number;
	verticalShaftIdYesNo: YesNo;
	oscillatingMovementsIdYesNo: YesNo;
	shockLoadsIdYesNo: YesNo;
	vibrationShockLoad?: number;
	requestData?: GreaseFrequencyConditionsPayload;
	validation: RelubricateConditionsValidationMap;
};

const validationFields: Array<keyof RelubricateConditionsState> = [
	'temperature',
	'rpm',
];

export const initialRelubricateConditionsState: RelubricateConditionsState = {
	loadDataID: greaseRelubricateLoad[ 0 ]?.id,
	verticalShaftIdYesNo: 'No',
	oscillatingMovementsIdYesNo: 'No',
	shockLoadsIdYesNo: 'No',
	validation: new Map( validationFields.map( ( key ) => [ key, false ] ) ),
};

export type RelubricateConditionsAction =
	| { type: 'change_load_id'; payload: number }
	| { type: 'change_number_field'; payload: [keyof RelubricateConditionsState, unknown] }
	| { type: 'change_boolean_field'; payload: [keyof RelubricateConditionsState, YesNo] }
	| { type: 'update_request_data' }
	| { type: 'reset'; payload: RelubricateConditionsState };

const getRelubricateConditionsFormData = ( state: RelubricateConditionsState, unit?: TimkenUnit|unknown): [RelubricateConditionsValidationMap, GreaseFrequencyConditionsPayload | undefined] => {
	const {
		temperature,
		rpm,
		loadDataID,
		verticalShaftIdYesNo,
		oscillatingMovementsIdYesNo,
		shockLoadsIdYesNo,
		vibrationShockLoad,
	} = state;

	const validation = new Map( state.validation );

	if ( unit == 'M') {
		validation.set( 'temperature', isValidRange( temperature, -40, 176 ) );
	} else {
		validation.set( 'temperature', isValidRange( temperature, -40, 350 ) );
	}

	validation.set( 'rpm', isValidRange( rpm, 0, 1000000 ) );

	const isFormValid = ! Array.from( validation.values() ).some( ( valid ) => ! valid );
	let requestData: GreaseFrequencyConditionsPayload | undefined;

	if ( isFormValid ) {
		requestData = {
			temperature: temperature || 0,
			rpm: rpm || 0,
			loadDataID,
			verticalShaftId: lubricationConditions.verticalShaft.id,
			oscillatingMovementsId: lubricationConditions.oscillatingMovements.id,
			shockLoadsId: lubricationConditions.shockLoads.id,
			verticalShaftIdYesNo: verticalShaftIdYesNo === 'Yes',
			oscillatingMovementsIdYesNo: oscillatingMovementsIdYesNo === 'Yes',
			shockLoadsIdYesNo: shockLoadsIdYesNo === 'Yes',
			vibrationShockLoad: shockLoadsIdYesNo === 'Yes' ? vibrationShockLoad : undefined,
		};
	}

	return [ validation, requestData ];
};

const greaseRelubricateConditionsReducer: React.Reducer<
	RelubricateConditionsState,
	RelubricateConditionsAction
> = ( state, action ) => {
	switch ( action.type ) {
		case 'change_load_id': {
			return {
				...state,
				loadDataID: action.payload,
			};
		}
		case 'change_number_field': {
			const [ key, value ] = action.payload;

			return {
				...state,
				[ key ]: convertNumberInput( value ),
			};
		}
		case 'change_boolean_field': {
			const [ key, value ] = action.payload;

			return {
				...state,
				[ key ]: value,
			};
		}
		case 'update_request_data': {
			const [ unit ] = action.payload;
			const [ validation, requestData ] = getRelubricateConditionsFormData( state, unit );

			return {
				...state,
				validation,
				requestData,
			};
		}
		case 'reset': {
			return {
				...action.payload,
			};
		}
	}

	return handleActionError( action );
};

export default greaseRelubricateConditionsReducer;
